import {
  Card,
  Chip,
  GroupsIcon,
  PersonIcon,
  Text,
  Tooltip,
} from '@billda/design-system';
import { format, parseISO } from 'date-fns';
import { PotStatusBadge } from '../PotStatusBadge';
import type { PotDetailsDisplayModel } from '../../types/potDetails.types';
import type { PotStatus } from '../../types/pot.types';

interface PotHeaderCardProps {
  potDetails: PotDetailsDisplayModel;
  wasLate?: boolean;
  submitDeadlineExtendedUntil?: string | null;
}

export function PotHeaderCard({
  potDetails,
  wasLate,
  submitDeadlineExtendedUntil,
}: PotHeaderCardProps) {
  const { owner, period, approvals, deadlines } = potDetails;

  const formatDateRange = (start: string, end: string) => {
    const startFormatted = format(parseISO(start), 'dd MMM');
    const endFormatted = format(parseISO(end), 'dd MMM yyyy');
    return `${startFormatted} – ${endFormatted}`;
  };

  const OwnerIcon = owner.ownerType === 'Gang' ? GroupsIcon : PersonIcon;

  return (
    <Card data-testid="pot-header-card" className="p-4 sm:p-6">
      <div className="mb-1 flex items-center gap-3">
        <OwnerIcon
          aria-hidden="true"
          className="size-6 text-content-secondary"
        />
        <Text variant="h6" className="font-semibold">
          {owner.ownerName}
        </Text>
        <PotStatusBadge status={approvals.status as PotStatus} />
        {wasLate && (
          <Tooltip content="Missed its submit deadline">
            <Chip
              label="Late"
              size="sm"
              variant="outlined"
              color="default"
              data-testid="pot-late-chip"
            />
          </Tooltip>
        )}
      </div>
      {submitDeadlineExtendedUntil && (
        <Text
          variant="body2"
          className="text-content-warning"
          data-testid="pot-extended-until"
        >
          Extended until{' '}
          {format(parseISO(submitDeadlineExtendedUntil), 'dd MMM yyyy')}
        </Text>
      )}
      {approvals.status === 'MissedVerificationDeadline' &&
        deadlines?.verifyBy && (
          <Text
            variant="body2"
            className="text-content-warning"
            data-testid="pot-verify-deadline"
          >
            Verify by {format(parseISO(deadlines.verifyBy), 'dd MMM yyyy')}
          </Text>
        )}
      <Text variant="body2" color="secondary">
        Week {period.weekNumber}, {period.year} &middot;{' '}
        {formatDateRange(period.start, period.end)}
      </Text>
    </Card>
  );
}
